/**
 * useTourAI
 *
 * AI integration layer for the guided tour system.
 * Bridges to AIAssistantContext for credits, session, and provider management.
 * Exposes: getAdaptiveHint(), answerTourQuestion(), suggestNextFlow(), generateStepSummary().
 * Falls back gracefully when AI credits are exhausted or the API is unavailable.
 */

'use client';

import { useCallback, useRef, useState } from 'react';
import { useAIAssistant } from '@/contexts/AIAssistantContext';
import { useGuidedTour } from '../GuidedTourProvider';
import { buildTourAIContext, type TourAIContextPayload } from '../ai/TourAIAdapter';
import type { TourFlow } from '@/config/guided-tours/types';
import type { AIMessage } from '@/components/shared/AIAssistantChat';

export interface TourAIHint {
  text: string;
  cached: boolean;
}

export interface TourFlowSuggestion {
  flowId: string;
  rationale: string;
}

export interface UseTourAIResult {
  aiAvailable: boolean;
  hintLoading: boolean;
  currentHint: TourAIHint | null;
  qaLoading: boolean;
  isGenerating: boolean;
  messages: AIMessage[];
  flowSuggestionLoading: boolean;
  quickGuideLoading: boolean;
  getAdaptiveHint: () => Promise<TourAIHint | null>;
  answerTourQuestion: (question: string) => Promise<string | null>;
  suggestNextFlow: (completedFlowIds: string[], availableFlows: TourFlow[]) => Promise<TourFlowSuggestion | null>;
  generateStepSummary: () => Promise<string[] | null>;
  generateQuickGuide: (flow: TourFlow) => Promise<string | null>;
  buildContext: () => TourAIContextPayload;
  clearHintCache: () => void;
}

interface UseTourAIOptions {
  industrySolution?: string | null;
  sandboxActive?: boolean;
  enabled?: boolean;
}

export function useTourAI(opts: UseTourAIOptions = {}): UseTourAIResult {
  const { industrySolution = null, sandboxActive = false, enabled = true } = opts;
  const {
    moduleKey,
    role,
    config,
    engine,
    currentStep,
    schema,
  } = useGuidedTour();

  const ai = useAIAssistant({
    moduleKey,
    moduleContext: 'guided-tour',
    enableSemanticContext: schema?.ai?.semantic_context ?? true,
    enableSemanticIntake: false,
    enableModuleAssistance: false,
  });

  const [hintLoading, setHintLoading] = useState(false);
  const [currentHint, setCurrentHint] = useState<TourAIHint | null>(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [flowSuggestionLoading, setFlowSuggestionLoading] = useState(false);
  const [quickGuideLoading, setQuickGuideLoading] = useState(false);
  const [aiDegraded, setAiDegraded] = useState(false);

  const hintCacheRef = useRef<Map<string, string>>(new Map());

  const aiConfig = schema?.ai ?? null;
  const aiAvailable = enabled && !aiDegraded && (ai.credits?.remaining ?? 0) > 0;

  const buildContext = useCallback((): TourAIContextPayload => {
    return buildTourAIContext({
      moduleKey,
      moduleName: config.moduleConfig?.module_name ?? moduleKey,
      flow: engine.selectedFlow,
      step: currentStep,
      stepIndex: engine.currentStepIndex,
      role,
      industrySolution,
      sandboxActive,
      aiConfig,
    });
  }, [
    moduleKey,
    config.moduleConfig,
    engine.selectedFlow,
    engine.currentStepIndex,
    currentStep,
    role,
    industrySolution,
    sandboxActive,
    aiConfig,
  ]);

  const getAdaptiveHint = useCallback(async (): Promise<TourAIHint | null> => {
    if (!aiAvailable || !currentStep?.ai?.generate_hint) {
      return null;
    }

    const stepId = currentStep.id;
    const cached = hintCacheRef.current.get(stepId);
    if (cached) {
      const hint: TourAIHint = { text: cached, cached: true };
      setCurrentHint(hint);
      return hint;
    }

    setHintLoading(true);
    try {
      const payload = buildContext();
      const res = await fetch('/api/v1/training/tour-ai-hint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'hint',
          context: payload,
        }),
      });

      if (!res.ok) {
        return null;
      }

      const data = await res.json();
      const hintText: string = data.hint ?? '';
      if (data.aiAvailable === false) {
        setAiDegraded(true);
      }
      if (!hintText) {
        return null;
      }

      hintCacheRef.current.set(stepId, hintText);
      const hint: TourAIHint = { text: hintText, cached: false };
      setCurrentHint(hint);
      return hint;
    } catch {
      return null;
    } finally {
      setHintLoading(false);
    }
  }, [aiAvailable, currentStep, buildContext]);

  const answerTourQuestion = useCallback(
    async (question: string): Promise<string | null> => {
      if (!aiAvailable || !question.trim()) {
        return null;
      }

      setQaLoading(true);
      try {
        const payload = buildContext();
        const contextualQuestion = `[Tour Context: ${payload.flowLabel} → ${payload.stepTitle}]\n\n${question}`;

        await ai.sendMessage(contextualQuestion);

        // The actual response is surfaced via the live `ai.messages` array;
        // this return value is intentionally best-effort for older callers.
        const lastAssistantMsg = [...ai.messages]
          .reverse()
          .find((m) => m.role === 'assistant');
        return lastAssistantMsg?.content ?? null;
      } catch {
        return null;
      } finally {
        setQaLoading(false);
      }
    },
    [aiAvailable, ai, buildContext]
  );

  const suggestNextFlow = useCallback(
    async (
      completedFlowIds: string[],
      availableFlows: TourFlow[]
    ): Promise<TourFlowSuggestion | null> => {
      if (!aiAvailable || availableFlows.length === 0) {
        return null;
      }

      const remainingFlows = availableFlows.filter(
        (f) => !completedFlowIds.includes(f.id)
      );
      if (remainingFlows.length === 0) {
        return null;
      }

      setFlowSuggestionLoading(true);
      try {
        const payload = buildContext();
        const res = await fetch('/api/v1/training/tour-ai-hint', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'suggest_flow',
            context: payload,
            completedFlowIds,
            availableFlows: remainingFlows.map((f) => ({
              id: f.id,
              label: f.label,
              summary: f.summary,
              kind: f.kind,
            })),
          }),
        });

        if (!res.ok) {
          return null;
        }

        const data = await res.json();
        if (data.aiAvailable === false) {
          setAiDegraded(true);
        }
        if (data.flowId && remainingFlows.some((f) => f.id === data.flowId)) {
          return {
            flowId: data.flowId,
            rationale: data.rationale ?? '',
          };
        }
        return null;
      } catch {
        return null;
      } finally {
        setFlowSuggestionLoading(false);
      }
    },
    [aiAvailable, buildContext]
  );

  const generateStepSummary = useCallback(async (): Promise<string[] | null> => {
    if (!aiAvailable || !currentStep?.ai?.adaptive_bullets) {
      return null;
    }

    try {
      const payload = buildContext();
      const res = await fetch('/api/v1/training/tour-ai-hint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'adaptive_bullets',
          context: payload,
        }),
      });

      if (!res.ok) {
        return null;
      }

      const data = await res.json();
      if (data.aiAvailable === false) {
        setAiDegraded(true);
      }
      const bullets: string[] = Array.isArray(data.bullets) ? data.bullets : [];
      return bullets.length > 0 ? bullets : null;
    } catch {
      return null;
    }
  }, [aiAvailable, currentStep, buildContext]);

  const generateQuickGuide = useCallback(
    async (flow: TourFlow): Promise<string | null> => {
      if (!aiAvailable || !flow.quick_guide?.ai_generate || flow.steps.length === 0) {
        return null;
      }

      setQuickGuideLoading(true);
      try {
        const payload = buildContext();
        const res = await fetch('/api/v1/training/tour-ai-hint', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'generate_quick_guide',
            context: payload,
            flowId: flow.id,
            flowLabel: flow.label,
            flowSummary: flow.summary,
            steps: flow.steps.map((s) => ({
              id: s.id,
              title: s.title,
              summary: s.summary,
              bullets: s.bullets,
            })),
          }),
        });

        if (!res.ok) {
          return null;
        }

        const data = await res.json();
        if (data.aiAvailable === false) {
          setAiDegraded(true);
        }
        const markdown: string = data.markdown ?? '';
        return markdown || null;
      } catch {
        return null;
      } finally {
        setQuickGuideLoading(false);
      }
    },
    [aiAvailable, buildContext]
  );

  const clearHintCache = useCallback(() => {
    hintCacheRef.current.clear();
    setCurrentHint(null);
  }, []);

  return {
    aiAvailable,
    hintLoading,
    currentHint,
    qaLoading,
    isGenerating: ai.isGenerating,
    messages: ai.messages,
    flowSuggestionLoading,
    quickGuideLoading,
    getAdaptiveHint,
    answerTourQuestion,
    suggestNextFlow,
    generateStepSummary,
    generateQuickGuide,
    buildContext,
    clearHintCache,
  };
}
